<template>
  <dara-single-content-layout container-size="lg">
    <template v-if="track" #content>
      <dara-header v-if="isEditing">
        {{ $t('editing-track') }}
      </dara-header>
      <dara-header v-else>
        {{ $t('new-track') }}
      </dara-header>
      <dara-p v-if="isEditing" class="dara-u-text--medium">
        {{ $t('editing-track-info1') }}<br>
        {{ $t('editing-track-info2') }}
      </dara-p>
      <dara-p v-else class="dara-u-text--medium">
        {{ $t('creating-track-info1') }}<br>
        {{ $t('creating-track-info2') }}
      </dara-p>

      <dara-article-search @addArticle="addArticle($event)" />
      <div v-if="articles.length > 0">
        <dara-header size="sm">
          {{ $t('chosen-articles') }}
        </dara-header>
        <dara-small>
          {{ $t('set-the-reading-order-of-articles') }}
        </dara-small>
      </div>
      <dara-draggable :data="articles" @addOrRemove="removeArticle($event)"></dara-draggable>
      <dara-track-edit-inputs @save="saveTrack" />
    </template>
  </dara-single-content-layout>
</template>

<script>
import { toastError } from '@/services/toastService';
import { goToOpenTrack, goToForbiddenTrack, goToTrackNotFound } from '@/modules/track/track.routes';
import { goHistoryBack } from '@/router/route.service';
import { saveTrack, getTrackById } from '@/modules/track/track.service';
import { fetchTrackArticles } from '@/modules/article/article.service';
import { translate } from '@/services/translate.service';
import { TRACK_ERRORS } from '@/modules/track/track.constants';
import { fetchDepartments } from '@/modules/department/department.service';
import { getters } from '@/modules/user/user.store';
import { USERS_ROLES } from '@/modules/user/user.constants';

import DaraArticleSearch from '@/modules/article/components/ArticleSearch.vue';
import DaraTrackEditInputs from '@/modules/track/components/TrackEditInputs.vue';
import DaraSingleContentLayout from '@/layouts/SingleContentLayout.vue';

export default {
  name: 'DaraTrackEditPage',
  components: {
    DaraArticleSearch,
    DaraTrackEditInputs,
    DaraSingleContentLayout,
  },
  provide() {
    const trackEditVm = {};
    Object.defineProperty(trackEditVm, 'track', {
      get: () => this.track,
    });
    return { trackEditVm };
  },
  data() {
    return {
      user: getters.getUser(),
      articleError: false,
      track: null,
      inputSearch: null,
      articles: [],
      departments: null,
    };
  },
  computed: {
    id() {
      return this.$route.params?.id;
    },
    isEditing() {
      return !!this.track?.id;
    },
  },
  mounted() {
    if (this.id) {
      if (this.user.permission < USERS_ROLES.WRITER) {
        goToForbiddenTrack(this.$router, false);
      } else {
        this.fetchTrack();
        this.fetchDepartments();
      }
    } else {
      if (this.user.permission < USERS_ROLES.WRITER) goToForbiddenTrack(this.$router, true);
      this.setNewTrack();
    }
  },
  methods: {
    goHistoryBack,
    fetchTrack() {
      if (this.track) return;
      getTrackById(this.id)
        .then(data => {
          this.track = data.data;
          if (!(this.user.permission > USERS_ROLES.WRITER || (this.user.permission === USERS_ROLES.WRITER && this.track.user.id === this.user.id))) {
            goToForbiddenTrack(this.$router, false);
          }
          this.fetchTrackArticles();
        })
        .catch(err => {
          this.track = null;
          if ((err.response.data.errors === TRACK_ERRORS[err.response.status] && err.response.status === 404)) {
            goToTrackNotFound(this.$router);
          } else if (err.response.data.errors === TRACK_ERRORS[err.response.status] && err.response.status === 403) {
            goToForbiddenTrack(this.$router, false);
          } else {
            toastError(`${translate('error-on-fetching-track')}`);
          }
        });
    },
    fetchTrackArticles() {
      fetchTrackArticles(this.track, {})
        .then(data => {
          this.articles = data.data;
          this.articles.forEach(article => {
            this.$set(article, 'id', article.id);
            this.$set(article, 'canInsert', this.canInsert(article));
          });
        })
        .catch(() => toastError(`${translate('error-getting-articles-from-track')}`));
    },
    fetchDepartments() {
      fetchDepartments()
        .then(data => {
          this.departments = data.data;
        })
        .catch(() => {
          this.departments = null;
        });
    },
    setNewTrack() {
      if (this.id) return;
      this.track = {
        articles: [],
      };
    },
    saveTrack() {
      this.track.articles = this.articles;

      if (!this.track.department_id) {
        toastError(`${translate('you-need-to-specify-the-track-department')}`);
        return;
      }

      let canSave = null;
      if (this.track.access === 0) {
        canSave = this.articles.map(article => {
          if (article.access === 2) {
            toastError(`${translate('there-is-a-personal-article-in-the-track')}`);
            return false;
          }
          if (article.access !== 0) {
            toastError(`${translate('there-is-a-non-public-article-in-the-track')}`);
            return false;
          }
          return true;
        });
      } else if (this.track.access === 1) {
        canSave = this.articles.map(article => {
          if (article.access === 2) {
            toastError(`${translate('there-is-a-personal-article-in-the-track')}`);
            return false;
          }
          if (article.department_id !== this.track.department_id && article.access !== 0) {
            toastError(`${translate('there-is-a-article-from-another-department-in-the-track')}`);
            return false;
          }
          return true;
        });
      }

      if (!canSave.includes(false)) {
        saveTrack(this.track)
          .then(article => {
            goToOpenTrack(this.$router, article || this.track);
          })
          .catch(err => {
            if ((err.response.data.errors === TRACK_ERRORS[err.response.status] && err.response.status === 401)) {
              toastError(`${translate('you-dont-have-permission-to-edit-this-track')}`);
            } else {
              toastError(`${translate('error-on-saving-track')}`);
            }
          });
      }
    },
    addArticle(articleAdded) {
      this.articles.forEach(article => {
        if (article.id === articleAdded.id) {
          this.articleError = true;
          toastError(`${translate('this-article-already-exists-in-the-track')}`);
        }
      });
      if (!this.articleError) {
        articleAdded.canInsert = this.canInsert;
        this.articles.push(articleAdded);
      }
      this.articleError = false;
    },
    removeArticle(index) {
      this.articles.splice(index, 1);
    },
    canInsert(article) {
      return !(article.access === 2 || (article.access !== 0 && this.track.access === 0)
        || (article.department_id !== this.track.department_id && this.track.access === 1 && article.access !== 0));
    },
  },
};
</script>
